import SelectInput from "@components/inputs/SelectInput"
import ModernVehicleSelector from "@components/NavbarHeader/components/MenuDesktop/ModernVehicleSelector"
import {
  getCarsMakeModelYearTrimUrl,
  getCarsMakeModelYearUrl,
} from "@utils/urls"
import _ from "lodash"
import Link from "next/link"
import { useRouter } from "next/router"
import React from "react"
import useGtmEvents from "@hooks/useGtmEvents"
import {
  Container,
  Content,
  Filter,
  Filters,
  Label,
  ModernVehicleWrapper,
  SectionSwitcher,
  TabsContainer,
  StyledTabsWithBorder,
  StyledTabWithBorder,
} from "./styles"

interface YMMProps {
  fontColor: string
  bgColor: string
  userModule?: string
}

const AUTOMOTIVE_URL = "/"
const LATE_MODEL_URL = "/late-model"
const POWERSPORTS_URL = "/powersports/"

const YmmSelector: React.FC<YMMProps> = ({
  fontColor,
  bgColor,
  userModule = "automotive",
}) => {
  const router = useRouter()
  const isLateModelPage = router.asPath === "/late-model"
  const { onVehicleSelectInteraction } = useGtmEvents()
  const layoutData = require("@data/layoutData")
  const cars = layoutData.allCars.filter((car) => {
    return car.module == "all" || car.module == userModule
  })
  const [make, setMake] = React.useState<string | "">("")
  const [model, setModel] = React.useState<string | "">("")
  const [trim, setTrim] = React.useState<string | "">("")
  const [year, setYear] = React.useState<string | "">("")

  // Tab state (only for automotive)
  const isAutomotive = userModule === "automotive"
  const [activeTab, setActiveTab] = React.useState<number>(
    isLateModelPage ? 1 : 0
  )

  React.useEffect(() => {
    setActiveTab(isLateModelPage ? 1 : 0)
  }, [isLateModelPage])

  const setCar = (event) => {
    window.scrollTo({ top: 20, behavior: "smooth" })
    if (trim) {
      router.push(getCarsMakeModelYearTrimUrl(make, model, year, trim))
    } else {
      router.push(getCarsMakeModelYearUrl(make, model, year))
    }
  }

  const makes = _.sortBy(
    _.uniqBy(
      cars.map(({ make }) => ({ label: make.name, value: make.slug })).sort(),
      "value"
    ),
    ["value"]
  ) as Array<any>

  const [models, setModels] = React.useState<Array<any>>([])

  const [trims, setTrims] = React.useState<Array<any>>([])

  const [years, setYears] = React.useState<Array<any>>([])

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 0 && isLateModelPage) {
      router.push(AUTOMOTIVE_URL)
      return
    }

    if (newValue === 1 && !isLateModelPage) {
      router.push(LATE_MODEL_URL)
      return
    }

    setActiveTab(newValue)
  }

  const onSelectMake = (make) => {
    const filteredModels = _.sortBy(
      _.uniqBy(
        cars
          .filter((car) => car.make.slug === make)
          .map(({ model }) => ({ label: model.name, value: model.slug })),
        "value"
      ),
      ["value"]
    )

    setMake(make)
    setModels(filteredModels)
    // setMake(make) // redundant duplicate
    setModel("")
    setTrim("")
    setYear("")
    setTrims([])
    setYears([])

    document.getElementById("Model")?.focus()

    if (make) {
      onVehicleSelectInteraction("vehicle_select_make", {
        flow: "shop_by_vehicle",
        source: "homepage",
        vehicle_type: "classic",
        make,
      })
    }
  }

  const onSelectModel = (model) => {
    if (model.trim() != "") {
      const filteredCars = _.sortBy(
        cars.filter(
          (car) => car.make.slug === make && car.model.slug === model
        ),
        ["slug"]
      )

      if (filteredCars.length > 1) {
        const trims = _.sortBy(
          _.uniqBy(
            filteredCars.map(({ trim }) => ({
              label: trim.name,
              value: trim.slug,
            })) as any,
            "value"
          ),
          ["value"]
        ) as any

        setModel(model)
        setTrim("")
        setTrims(trims)
        setYears([])

        document.getElementById("Trim")?.focus()
      } else {
        const thisCar = filteredCars[0]
        const filteredYears = thisCar.years.split(", ").map((year) => ({
          label: year.trim(),
          value: year.trim(),
        }))

        setModel(model)
        setYear("")
        setTrim("")
        setTrims([])
        setYears(filteredYears)

        document.getElementById("Year")?.focus()
      }
    } else {
      setModel("")
      setTrims([])
      setYears([])
    }

    if (model) {
      onVehicleSelectInteraction("vehicle_select_model", {
        flow: "shop_by_vehicle",
        source: "homepage",
        vehicle_type: "classic",
        make,
        model,
      })
    }
  }

  const onSelectTrim = (trim) => {
    const thisCar = cars.find((car) => {
      return (
        car.make.slug === make &&
        car.model.slug === model &&
        car.trim.slug === trim
      )
    })

    if (thisCar) {
      const filteredYears = thisCar.years.split(", ").map((year) => ({
        label: year.trim(),
        value: year.trim(),
      }))
      setTrim(trim)
      setYears(filteredYears)
    }
  }

  const renderContent = () => {
    return (
      <Content>
        <Filters>
          <Filter>
            <SelectInput
              name="Make"
              label="Make"
              value={make}
              options={makes}
              onChange={(e) => {
                onVehicleSelectInteraction("vehicle_dropdown_open", {
                  flow: "shop_by_vehicle",
                  source: "homepage",
                  vehicle_type: "classic",
                  field: "make",
                })
                onSelectMake(e.target.value)
              }}
            />
          </Filter>
          <Filter disabled={!make}>
            <SelectInput
              name={"Model"}
              label={"Model"}
              value={model}
              options={models}
              onChange={(e) => {
                onVehicleSelectInteraction("vehicle_dropdown_open", {
                  flow: "shop_by_vehicle",
                  source: "homepage",
                  vehicle_type: "classic",
                  field: "model",
                })
                onSelectModel(e.target.value)
              }}
            />
          </Filter>
          {trims?.length > 0 && (
            <Filter disabled={!make || !model}>
              <SelectInput
                name={"Trim"}
                label={"Trim"}
                value={trim}
                options={trims}
                onChange={(e) => {
                  onVehicleSelectInteraction("vehicle_dropdown_open", {
                    flow: "shop_by_vehicle",
                    source: "homepage",
                    vehicle_type: "classic",
                    field: "trim",
                  })
                  onSelectTrim(e.target.value)
                }}
              />
            </Filter>
          )}
          <Filter
            disabled={
              trims?.length > 0 ? !make || !model || !trim : !make || !model
            }
          >
            <SelectInput
              name={"Year"}
              label={"Year"}
              value={year}
              options={years}
              onChange={(e) => {
                setYear(e.target.value)
                if (e.target.value) {
                  onVehicleSelectInteraction("vehicle_select_year", {
                    flow: "shop_by_vehicle",
                    source: "homepage",
                    vehicle_type: "classic",
                    make,
                    model,
                    year: e.target.value,
                  })
                }
              }}
            />
          </Filter>
          <button
            disabled={make == "" || model == "" || year == ""}
            onClick={(e) => {
              onVehicleSelectInteraction("vehicle_click_build", {
                flow: "shop_by_vehicle",
                source: "homepage",
                vehicle_type: "classic",
                make,
                model,
                year,
                ...(trim ? { trim } : {}),
              })
              setCar(e)
            }}
          >
            Build
          </button>
        </Filters>
      </Content>
    )
  }

  return (
    <Container bgColor={bgColor}>
      <Label fontColor={fontColor}>
        <div>Shop by vehicle</div>
        <h1>Let's start that project</h1>
      </Label>
      <SectionSwitcher fontColor={fontColor}>
        <Link
          className={
            userModule == "automotive" && !isLateModelPage ? "active" : ""
          }
          href={AUTOMOTIVE_URL}
        >
          Restomod
        </Link>
        <Link className={isLateModelPage ? "active" : ""} href={LATE_MODEL_URL}>
          <>
            <span className="desktop-label">New Vehicle</span>
            <span className="mobile-label">New Vehicle</span>
          </>
        </Link>
        <Link
          className={userModule == "powersports" ? "active" : ""}
          href={POWERSPORTS_URL}
        >
          Powersports
        </Link>
      </SectionSwitcher>
      <TabsContainer>
        {isAutomotive && (
          <StyledTabsWithBorder
            value={activeTab}
            onChange={handleTabChange}
            aria-label="vehicle type tabs"
          >
            <StyledTabWithBorder label="1947-2017" />
            <StyledTabWithBorder
              label={
                <span style={{ whiteSpace: "nowrap", textTransform: "none" }}>
                  2015 - PRESENT
                </span>
              }
            />
          </StyledTabsWithBorder>
        )}
        {isAutomotive ? (
          <div>
            {activeTab === 0 && renderContent()}
            {activeTab === 1 && (
              <ModernVehicleWrapper>
                <ModernVehicleSelector
                  cars={cars}
                  module={userModule}
                  showPopularBuilds={false}
                />
              </ModernVehicleWrapper>
            )}
          </div>
        ) : (
          renderContent()
        )}
      </TabsContainer>
      {/* Duplicate block (redundant - same tabs + content rendered twice):
      {isAutomotive && (
        <TabsContainer>
          <StyledTabsWithBorder
            value={activeTab}
            onChange={handleTabChange}
            aria-label="vehicle type tabs"
          >
            <StyledTabWithBorder label="1947-2017" />
            <StyledTabWithBorder
              label={
                <span style={{ whiteSpace: "nowrap", textTransform: "none" }}>
                  2018 - Present
                </span>
              }
            />
          </StyledTabsWithBorder>
        </TabsContainer>
      )}
      {isAutomotive ? (
        <div>
          {activeTab === 0 && renderContent()}
          {activeTab === 1 && (
            <ModernVehicleWrapper>
              <ModernVehicleSelector
                cars={cars}
                module={userModule}
                showPopularBuilds={false}
              />
            </ModernVehicleWrapper>
          )}
        </div>
      ) : (
        renderContent()
      )}
      */}
    </Container>
  )
}

export default YmmSelector
